import { useToast } from "@/hooks/use-toast"
import { X, CheckCircle2, AlertCircle, Info } from "lucide-react"
import { cn } from "@/lib/utils"

export function Toaster() {
  const { toasts, dismiss } = useToast()

  return (
    <div className="fixed top-0 left-1/2 -translate-x-1/2 z-[100] w-full max-w-[420px] p-4 pt-4 md:pt-6">
      {toasts.map((toast) => {
        const { id, title, description, variant } = toast;
        const isDestructive = variant === "destructive";

        // Determinar ícone e cores baseado no título/variante
        const isSuccess = title?.toLowerCase().includes('sucesso') ||
                         title?.toLowerCase().includes('success') ||
                         title?.toLowerCase().includes('copiado');
        const isError = isDestructive ||
                       title?.toLowerCase().includes('erro') ||
                       title?.toLowerCase().includes('error');

        const Icon = isError ? AlertCircle : isSuccess ? CheckCircle2 : Info;

        return (
          <div
            key={id}
            data-state="open"
            className={cn(
              "group pointer-events-auto relative mb-3 flex w-full items-start gap-3 overflow-hidden rounded-xl p-4 pr-10 shadow-2xl transition-all",
              "animate-in slide-in-from-top fade-in-80 data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-top",
              // Glassmorphism base
              "backdrop-blur-xl backdrop-saturate-150",
              // Variantes de cor
              isError
                ? "bg-red-950/80 border border-red-500/30 shadow-red-500/20"
                : isSuccess
                ? "bg-emerald-950/80 border border-emerald-500/30 shadow-emerald-500/20"
                : "bg-zinc-900/80 border border-purple-500/20 shadow-purple-500/10"
            )}
            style={{
              boxShadow: isError
                ? '0 8px 32px rgba(239, 68, 68, 0.15), 0 0 0 1px rgba(239, 68, 68, 0.1)'
                : isSuccess
                ? '0 8px 32px rgba(16, 185, 129, 0.15), 0 0 0 1px rgba(16, 185, 129, 0.1)'
                : '0 8px 32px rgba(139, 92, 246, 0.1), 0 0 0 1px rgba(139, 92, 246, 0.05)',
            }}
          >
            {/* Ícone */}
            <div className={cn(
              "flex-shrink-0 mt-0.5",
              isError ? "text-red-400" : isSuccess ? "text-emerald-400" : "text-purple-400"
            )}>
              <Icon className="h-5 w-5" />
            </div>

            {/* Conteúdo */}
            <div className="grid gap-1 flex-1">
              {title && (
                <div className={cn(
                  "text-sm font-semibold",
                  isError ? "text-red-100" : isSuccess ? "text-emerald-100" : "text-zinc-100"
                )}>
                  {title}
                </div>
              )}
              {description && (
                <div className={cn(
                  "text-sm",
                  isError ? "text-red-200/80" : isSuccess ? "text-emerald-200/80" : "text-zinc-300/80"
                )}>
                  {description}
                </div>
              )}
            </div>

            {/* Botão fechar */}
            <button
              onClick={() => dismiss(id)}
              className={cn(
                "absolute right-3 top-3 rounded-md p-1.5 transition-all opacity-60 hover:opacity-100",
                "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent",
                isError
                  ? "text-red-300 hover:bg-red-500/20 focus:ring-red-500/50"
                  : isSuccess
                  ? "text-emerald-300 hover:bg-emerald-500/20 focus:ring-emerald-500/50"
                  : "text-zinc-400 hover:bg-white/10 focus:ring-purple-500/50"
              )}
            >
              <X className="h-4 w-4" />
            </button>

            {/* Barra de progresso sutil (decorativa) */}
            <div className={cn(
              "absolute bottom-0 left-0 right-0 h-0.5 opacity-30",
              isError ? "bg-gradient-to-r from-red-500 via-red-400 to-transparent"
                : isSuccess ? "bg-gradient-to-r from-emerald-500 via-emerald-400 to-transparent"
                : "bg-gradient-to-r from-purple-500 via-purple-400 to-transparent"
            )} />
          </div>
        );
      })}
    </div>
  )
}
